import { trackEventServerSide } from "helpers/api";
import { getMajorVersion } from "helpers/services/mixpanel";
import {
  ForkThreadEvent,
  MixpanelActionSource,
  MixpanelEvent,
  NewThreadInteractionEvent,
  OpenShareThreadEvent,
  SearchEvent,
  ShareThreadEvent,
  ThreadProperties,
  ToggleThreadShareEvent,
  ViewThreadEvent,
} from "helpers/services/mixpanel/events";
import { useNewSearchSettingsProperties } from "hooks/useNewSearchSettingsProperties";
import useSearchMode from "hooks/useSearchMode";
import { useAppSelector } from "hooks/useStore";
import { Thread } from "../types";
/**
 * A hook that provides callbacks to track events related to a thread interaction.
 *
 * @param threadId - The ID of the thread
 * @returns callback functions to track events related to a thread interaction
 */
export const useThreadEvents = (thread?: Thread) => {
  // Get data from existing hooks
  const navigationSource = useAppSelector(
    (state) => state.analytic.navigationSource
  );
  const { newSearchSettingsProperties } = useNewSearchSettingsProperties();
  const { currentSearchMode } = useSearchMode();
  // Create event properties
  const threadProperties: ThreadProperties = {
    threadId: thread?.thread_id,
    threadTitle: thread?.title,
    threadInteractionCount: thread?.interactions.length,
  };

  // Event trackers
  const trackToggleThreadShare = (value: boolean) => {
    if (!thread) return;

    const eventData: ToggleThreadShareEvent = {
      event: MixpanelEvent.ToggleThreadShare,
      ...threadProperties,
      value,
      version: getMajorVersion(),
    };

    trackEventServerSide(eventData);
  };

  const trackShareThread = (shareType: string) => {
    if (!thread) return;

    const eventData: ShareThreadEvent = {
      event: MixpanelEvent.ShareThread,
      ...threadProperties,
      shareType,
      version: getMajorVersion(),
    };

    trackEventServerSide(eventData);
  };

  const trackOpenShareThread = () => {
    if (!thread) return;

    const eventData: OpenShareThreadEvent = {
      event: MixpanelEvent.OpenShareThread,
      ...threadProperties,
      version: getMajorVersion(),
    };

    trackEventServerSide(eventData);
  };

  const trackSearch = (
    query: string,
    actionSource: MixpanelActionSource,
    isRelatedSearch?: boolean
  ) => {
    if (!thread) return;

    const eventData: SearchEvent = {
      event: MixpanelEvent.Search,
      ...threadProperties,
      ...newSearchSettingsProperties,
      navigationSource,
      query,
      version: getMajorVersion(),
      actionSource,
      isRelatedSearch: isRelatedSearch,
      isNewThread: false,
      isThreadFollowUp: true,
      searchMode: currentSearchMode,
    };

    trackEventServerSide(eventData);
  };

  const trackViewThread = () => {
    if (!thread) return;

    const eventData: ViewThreadEvent = {
      event: MixpanelEvent.ViewThread,
      ...threadProperties,
      isOwner: thread?.can_edit ?? false,
      version: getMajorVersion(),
    };

    trackEventServerSide(eventData);
  };

  const trackAddInteraction = (
    userMessage: string,
    actionSource: MixpanelActionSource
  ) => {
    if (!thread) return;

    const eventData: NewThreadInteractionEvent = {
      event: MixpanelEvent.NewThreadInteraction,
      ...threadProperties,
      ...newSearchSettingsProperties,
      query: userMessage,
      version: getMajorVersion(),
      actionSource,
      searchMode: currentSearchMode,
    };

    trackEventServerSide(eventData);
  };

  const trackForkThread = (
    userMessage: string,
    actionSource: MixpanelActionSource
  ) => {
    if (!thread) return;

    const eventData: ForkThreadEvent = {
      event: MixpanelEvent.ForkThread,
      ...threadProperties,
      ...newSearchSettingsProperties,
      query: userMessage,
      version: getMajorVersion(),
      actionSource,
    };

    trackEventServerSide(eventData);
  };

  return {
    trackToggleThreadShare,
    trackShareThread,
    trackOpenShareThread,
    trackSearch,
    trackViewThread,
    trackAddInteraction,
    trackForkThread,
  };
};
